/**
 * Web twin of `CvPresetSheet`. Same contract and copy, rendered as a centered
 * `WebDialog` (the shared web dialog shell) rather than the native bottom sheet.
 *
 * Renders the unified preset + catalog-skill row list from `useCvPreset`
 * (curated presets first, then every catalog CV skill with real localized
 * copy and flag artwork). Like the native sheet, tapping a row applies it and
 * closes: there is no confirm step.
 */
import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, primaryShades } from '@tabbio/tokens';
import { Check, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useLocale } from '../../hooks/useLocale';
import { getSearchTextDirection } from '../../lib/search-text-direction';
import { WebDialog } from '../common/WebDialog.web';
import { formatSkillCount } from '../skills/skill-name';
import { SkillCatalogIcon } from '../skills/SkillCatalogIcon';
import { CvPresetIcon } from './CvPresetIcon';
import {
  filterCvPresetRows,
  type CvPresetRow,
  type CvPresetRowEntry,
} from './cv-preset-rows';

interface Props {
  visible: boolean;
  onClose: () => void;
  appliedPresetId: string | null;
  entries: CvPresetRowEntry[];
  onApply: (row: CvPresetRow) => void;
  isApplying?: boolean;
}

const SEARCH_THRESHOLD = 4;

export function CvPresetSheet({
  visible,
  onClose,
  appliedPresetId,
  entries,
  onApply,
  isApplying = false,
}: Props) {
  const { t } = useTranslation('auth');
  const { isDark } = useTheme();
  const { isRTL } = useLocale();
  const [query, setQuery] = useState('');
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const visibleRows = useMemo(
    () => filterCvPresetRows(entries, query),
    [entries, query],
  );

  // Re-opening the dialog starts from a clean search.
  const [lastVisible, setLastVisible] = useState(visible);
  if (visible !== lastVisible) {
    setLastVisible(visible);
    if (visible) setQuery('');
  }

  if (!visible) return null;

  const flexDirection = 'row';
  const textAlign = isRTL ? 'right' : 'left';
  const Chevron = isRTL ? ChevronLeft : ChevronRight;
  const selectedBg = isDark ? 'rgba(94,41,255,0.18)' : primaryShades[50];

  return (
    <WebDialog
      title={t('onboarding.cvPreset.sheetTitle', {
        defaultValue: 'Choose a Custom CV',
      })}
      subtitle={t('onboarding.cvPreset.sheetSubtitle', {
        defaultValue:
          'Built by career experts and the community. Each one changes how your CV is written, section by section.',
      })}
      onClose={onClose}
      maxWidth={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {entries.length > SEARCH_THRESHOLD ? (
          <div style={{ ...searchRowStyle, flexDirection }}>
            <Search size={16} strokeWidth={1.9} color="var(--text-tertiary)" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('onboarding.cvPreset.searchPlaceholder', {
                defaultValue: 'Search custom CVs',
              })}
              style={{
                ...searchInputStyle,
                textAlign: getSearchTextDirection(query, isRTL).textAlign,
              }}
            />
          </div>
        ) : null}

        {visibleRows.length === 0 ? (
          <p style={{ ...emptyStyle, textAlign }}>
            {t('onboarding.cvPreset.searchEmpty', {
              defaultValue: 'No custom CVs match that search.',
            })}
          </p>
        ) : (
          <div role="radiogroup" style={listStyle}>
            {visibleRows.map(({ row, label, description }) => {
              const active = row.id === appliedPresetId;
              return (
                <button
                  key={row.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={isApplying}
                  onClick={() => onApply(row)}
                  onMouseEnter={() => setHoveredId(row.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    ...rowStyle,
                    flexDirection,
                    backgroundColor: active
                      ? selectedBg
                      : hoveredId === row.id
                        ? 'var(--hover)'
                        : 'transparent',
                    borderColor: active ? colors.primary : 'var(--input-border)',
                    cursor: isApplying ? 'default' : 'pointer',
                    opacity: isApplying && !active ? 0.5 : 1,
                  }}
                >
                  {row.kind === 'skill' ? (
                    <SkillCatalogIcon skill={row.skill} size={30} />
                  ) : (
                    <CvPresetIcon icon={row.preset.icon} size={30} />
                  )}
                  <span style={{ ...copyStyle, textAlign }}>
                    <span dir="auto" style={nameStyle}>
                      {label}
                    </span>
                    {description ? (
                      <span dir="auto" style={descriptionStyle}>
                        {description}
                      </span>
                    ) : null}
                    {row.kind === 'skill' ? (
                      <span style={installsStyle}>
                        {/* Same author · installs meta line as CvSkillPicker,
                            so this reads as the one catalog across surfaces. */}
                        {row.skill.authorName ||
                          t('common:skills.byTabbioName')}
                        {' · '}
                        {t('common:skills.usageCount', {
                          value: formatSkillCount(row.skill.usageCount),
                        })}
                      </span>
                    ) : null}
                  </span>
                  {active ? (
                    <span
                      style={{ ...checkStyle, backgroundColor: colors.primary }}
                    >
                      <Check size={12} strokeWidth={3} color="#ffffff" />
                    </span>
                  ) : (
                    <Chevron
                      size={18}
                      strokeWidth={1.9}
                      color="var(--text-tertiary)"
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </WebDialog>
  );
}

const searchRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  padding: '10px 16px',
  boxSizing: 'border-box',
};

const searchInputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontFamily: 'inherit',
  fontSize: 14.5,
  color: 'var(--text-primary)',
  padding: 0,
};

const emptyStyle: CSSProperties = {
  margin: 0,
  fontSize: 13.5,
  lineHeight: '19px',
  color: 'var(--text-secondary)',
  padding: '8px 0',
};

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
  borderRadius: 16,
  borderWidth: 1.5,
  borderStyle: 'solid',
  padding: 12,
  textAlign: 'inherit',
  fontFamily: 'inherit',
  transition: 'background-color 150ms, border-color 150ms',
  boxSizing: 'border-box',
};

const copyStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const nameStyle: CSSProperties = {
  fontSize: 14.5,
  fontWeight: 500,
  color: 'var(--text-primary)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const descriptionStyle: CSSProperties = {
  fontSize: 12.5,
  lineHeight: '17px',
  color: 'var(--text-secondary)',
};

const installsStyle: CSSProperties = {
  marginTop: 2,
  color: 'var(--text-tertiary)',
  fontSize: 11.5,
  lineHeight: '15px',
};
const checkStyle: CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: 10,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};
